import React, {useState} from 'react';

import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';
import {FormGroup, TextArea} from '@udemy/react-form-components';

import {AT_RISK_STEP_ID, FREEFORM_MAX_CHARS, STEP_PROGRESS} from '../constants';
import {AT_RISK_HELP_CTA} from '../events';
import {StepLayout} from '../step-layout.react-component';
import styles from './something-else-step.module.less';

import type {StepRendererProps} from '../types';

export function SomethingElseStep({
    state,
    dispatch,
    onClose,
    onTrackEventClick,
    submitAnswer,
}: StepRendererProps) {
    const {gettext, interpolate} = useI18n();
    const [localText, setLocalText] = useState(state.freeformText);

    const handleSubmit = async () => {
        dispatch({type: 'FREEFORM_UPDATED', text: localText});
        dispatch({type: 'SUBMITTING'});
        try {
            await submitAnswer('something_else', localText);
            onTrackEventClick(AT_RISK_HELP_CTA.SUBMIT_FEEDBACK);
            dispatch({type: 'STEP_ADVANCED', nextStepId: AT_RISK_STEP_ID.CONFIRMATION});
        } catch {
            dispatch({
                type: 'SUBMIT_ERROR',
                error: gettext('Something went wrong. Please try again.'),
            });
        }
    };

    const charsRemaining = FREEFORM_MAX_CHARS - localText.length;

    return (
        <StepLayout
            progressValue={STEP_PROGRESS[AT_RISK_STEP_ID.SOMETHING_ELSE]}
            onClose={onClose}
            footerLeftChildren={
                <Button
                    udStyle="ghost"
                    size="medium"
                    onClick={() =>
                        dispatch({type: 'STEP_ADVANCED', nextStepId: AT_RISK_STEP_ID.INITIAL})
                    }
                >
                    {gettext('Back')}
                </Button>
            }
            footerRightChildren={
                <Button
                    udStyle="primary"
                    size="medium"
                    disabled={localText.trim().length === 0 || state.isSubmitting}
                    onClick={handleSubmit}
                >
                    {state.isSubmitting ? gettext('Submitting…') : gettext('Submit')}
                </Button>
            }
        >
            <div className={styles['content']}>
                <h2 className="ud-heading-xl">{gettext("Tell us what's going on.")}</h2>
                <p className="ud-text-md">
                    {gettext(
                        "We read every response. Whatever's on your mind, it helps us understand how to do better.",
                    )}
                </p>
                {state.submitError && (
                    <p className={styles['error']} role="alert">
                        {state.submitError}
                    </p>
                )}
                <FormGroup
                    label={gettext('Your feedback')}
                    labelProps={{className: 'ud-sr-only'}}
                    note={interpolate(
                        gettext('%(charsRemaining)s characters remaining'),
                        {charsRemaining},
                        true,
                    )}
                >
                    <TextArea
                        value={localText}
                        placeholder={gettext("What's going on for you?")}
                        maxLength={FREEFORM_MAX_CHARS}
                        rows={5}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setLocalText(e.target.value)
                        }
                    />
                </FormGroup>
            </div>
        </StepLayout>
    );
}
